import { Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'

export interface ApplicationFee {
  account: number
  fee_count: number
  date: string
  totals: {
    [currency: string]: number
  }
  archived: number
  stripe_id: string
  created: number
  pkey: string
}

export interface MonthlyApplicationFee {
  month: string // YYYY-MM format
  fee_count: number
  totals: {
    [currency: string]: number
  }
  stripe_id: string
  items: ApplicationFee[]
}

@Injectable()
export class ApplicationFeesService {
  constructor(private readonly http: HttpClient) {}

  async getFees(month?: string) {
    let startDate: string
    let endDate: string

    if (month) {
      // If month is provided in YYYY-MM format, get start and end dates for that month
      const [year, monthNum] = month.split('-').map((num) => parseInt(num))
      const startOfMonth = new Date(year, monthNum - 1, 1) // Month is 0-indexed
      const endOfMonth = new Date(year, monthNum, 0) // Last day of the month

      startDate = this.formatDate(startOfMonth)
      endDate = this.formatDate(endOfMonth)
    } else {
      // Default to October 2024 to current date
      const today = new Date()
      const startOfOctober2024 = new Date(2024, 9, 1) // Month is 0-indexed, so 9 = October

      startDate = this.formatDate(startOfOctober2024)
      endDate = this.formatDate(today)
    }

    const url = `@api/reports/application-fees?start_date=${startDate}&end_date=${endDate}`
    const response = await this.http.get(url).toPromise()

    const fees = (response?.['data'] as ApplicationFee[]) || []
    return this.groupFeesByMonth(fees)
  }

  private formatDate(date: Date): string {
    return date.toISOString().split('T')[0] // YYYY-MM-DD format
  }

  private groupFeesByMonth(fees: ApplicationFee[]): MonthlyApplicationFee[] {
    if (!fees || fees.length === 0) return []

    // Group fees by month
    const monthlyGroups: { [key: string]: ApplicationFee[] } = {}

    fees.forEach((fee) => {
      const date = new Date(fee.date)
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`

      if (!monthlyGroups[monthKey]) {
        monthlyGroups[monthKey] = []
      }

      monthlyGroups[monthKey].push(fee)
    })

    // Convert groups to MonthlyApplicationFee objects
    return Object.entries(monthlyGroups)
      .map(([monthKey, monthFees]) => {
        // Aggregate totals
        const totals: { [currency: string]: number } = {}
        let feeCount = 0

        monthFees.forEach((fee) => {
          feeCount += fee.fee_count

          if (fee.totals) {
            Object.entries(fee.totals).forEach(([currency, amount]) => {
              if (!totals[currency]) {
                totals[currency] = 0
              }
              totals[currency] += amount
            })
          }
        })

        return {
          month: monthKey, // Already in YYYY-MM format
          fee_count: feeCount,
          totals,
          stripe_id: monthFees[0]?.stripe_id || '',
          items: monthFees,
        }
      })
      .sort((a, b) => b.month.localeCompare(a.month)) // Sort by month descending
  }
}
